import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { OnboardingProvider } from "./components/onboarding";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import ErrorBoundary, { PageErrorBoundary } from "./components/ErrorBoundary";
import RetirementOptimizer from "./pages/RetirementOptimizer";
import QuarterlyTax from "./pages/QuarterlyTax";
import CashFlow from "./pages/CashFlow";
import LandingES from "./pages/LandingES";
import StrategyReport from "./pages/StrategyReport";
import ReportHistory from "./pages/ReportHistory";
import { initSentry, setSentryUser, sendTestEvent } from "./config/sentry";
import { logError } from "./utils/logger";
import { useMobileAppInit } from "./hooks/useMobileAppInit";
import { isNativePlatform } from "./utils/capacitor";

// Initialize Sentry error monitoring
initSentry();

// Send a test event to Sentry on first load (will only show once in Sentry)
if (process.env.REACT_APP_SENTRY_DSN) {
  sendTestEvent();
}

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import Deductions from "./pages/Deductions";
import QuarterlyEstimates from "./pages/QuarterlyEstimates";
import Documents from "./pages/Documents";
import Chat from "./pages/Chat";
import Settings from "./pages/Settings";
import Transactions from "./pages/Transactions";
import Reports from "./pages/Reports";
import Receipts from "./pages/Receipts";
import Businesses from "./pages/Businesses";
import AuditReport from "./pages/AuditReport";
import Mileage from "./pages/Mileage";
import Contractors from "./pages/Contractors";
import TaxPackage from "./pages/TaxPackage";
import Pricing from "./pages/Pricing";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import CpaReview from "./pages/CpaReview";
import Referrals from "./pages/Referrals";
import AdminDashboard from "./pages/AdminDashboard";
import BankConnections from "./pages/BankConnections";
import Help from "./pages/Help";
import Strategy from "./pages/Strategy";
import StrategyAdvisor from "./pages/StrategyAdvisor";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Waitlist from "./pages/Waitlist";
import FoundingCheckout from "./pages/FoundingCheckout";
import FoundingSuccess from "./pages/FoundingSuccess";
import HowItWorks from "./pages/HowItWorks";
import MetaLanding from "./pages/MetaLanding";
import MediaKit from "./pages/MediaKit";
import Intake from "./pages/Intake";
import FounderKit from "./pages/FounderKit";
import QuizFunnel from "./pages/QuizFunnel";
import AuditLanding from "./pages/AuditLanding";
import AuditCheckout from "./pages/AuditCheckout";
import AuditFounding from "./pages/AuditFounding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route Component with Subscription Check
const ProtectedRoute = ({ children, requireSubscription = true }) => {
  const { isAuthenticated, loading, user, getAuthHeaders } = useAuth();
  const location = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated || !user) {
        setCheckingSubscription(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/subscription/status`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        setSubscriptionStatus(response.data);
      } catch (error) {
        logError(error, { context: 'App.subscriptionCheck' });
        // On error, allow access (fail open for better UX)
        setSubscriptionStatus({ is_active: true });
      } finally {
        setCheckingSubscription(false);
      }
    };

    if (isAuthenticated && user) {
      checkSubscription();
    } else {
      setCheckingSubscription(false);
    }
  }, [isAuthenticated, user]);

  if (loading || checkingSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check subscription for protected routes (skip for settings page)
  if (requireSubscription && subscriptionStatus && !subscriptionStatus.is_active) {
    return <Navigate
      to="/pricing"
      state={{
        from: location,
        plan_type: subscriptionStatus.plan_type,
        days_until_deletion: subscriptionStatus.days_until_deletion,
      }}
      replace
    />;
  }

  return (
    <OnboardingProvider>
      {children}
    </OnboardingProvider>
  );
};

// App Router with session detection
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL fragment (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/intake" element={<ProtectedRoute requireSubscription={false}><Intake /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/cpa-review/:token" element={<CpaReview />} />
      
      {/* Protected Routes - wrapped with Error Boundaries */}
      <Route path="/dashboard" element={<ProtectedRoute><PageErrorBoundary pageName="Dashboard"><Dashboard /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/deductions" element={<ProtectedRoute><PageErrorBoundary pageName="Deductions"><Deductions /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><PageErrorBoundary pageName="Transactions"><Transactions /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/quarterly" element={<ProtectedRoute><PageErrorBoundary pageName="Quarterly Estimates"><QuarterlyEstimates /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><PageErrorBoundary pageName="Documents"><Documents /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/receipts" element={<ProtectedRoute><PageErrorBoundary pageName="Receipts"><Receipts /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><PageErrorBoundary pageName="Reports"><Reports /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/businesses" element={<ProtectedRoute><PageErrorBoundary pageName="Businesses"><Businesses /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><PageErrorBoundary pageName="AI Chat"><Chat /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute requireSubscription={false}><PageErrorBoundary pageName="Settings"><Settings /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute requireSubscription={false}><PageErrorBoundary pageName="Referrals"><Referrals /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/audit-report" element={<ProtectedRoute><PageErrorBoundary pageName="Audit Report"><AuditReport /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/mileage" element={<ProtectedRoute><PageErrorBoundary pageName="Mileage"><Mileage /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/contractors" element={<ProtectedRoute><PageErrorBoundary pageName="Contractors"><Contractors /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/tax-package" element={<ProtectedRoute><PageErrorBoundary pageName="Tax Package"><TaxPackage /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/strategy" element={<ProtectedRoute><PageErrorBoundary pageName="Strategy"><Strategy /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/strategy-advisor" element={<ProtectedRoute><PageErrorBoundary pageName="Strategy Advisor"><StrategyAdvisor /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/strategy-advisor/reports" element={<ProtectedRoute><PageErrorBoundary pageName="Report History"><ReportHistory /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/strategy-advisor/report/:reportId" element={<ProtectedRoute><PageErrorBoundary pageName="Strategy Report"><StrategyReport /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/bank-connections" element={<ProtectedRoute><PageErrorBoundary pageName="Bank Connections"><BankConnections /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><PageErrorBoundary pageName="Help"><Help /></PageErrorBoundary></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><PageErrorBoundary pageName="Admin Dashboard"><AdminDashboard /></PageErrorBoundary></ProtectedRoute>} />
      {/* Feature 1 — Retirement Optimizer */}
      <Route path="/tools/retirement" element={<ProtectedRoute><PageErrorBoundary pageName="Retirement Optimizer"><RetirementOptimizer /></PageErrorBoundary></ProtectedRoute>} />
      {/* Feature 5 — Quarterly Tax Command Center */}
      <Route path="/quarterly-tax" element={<ProtectedRoute><PageErrorBoundary pageName="Quarterly Tax"><QuarterlyTax /></PageErrorBoundary></ProtectedRoute>} />
      {/* Feature 6 — Cash Flow */}
      <Route path="/cash-flow" element={<ProtectedRoute><PageErrorBoundary pageName="Cash Flow"><CashFlow /></PageErrorBoundary></ProtectedRoute>} />
      
      {/* Legal Pages - Public Access */}
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      
      {/* Waitlist - Public Access */}
      <Route path="/waitlist" element={<Waitlist />} />
      <Route path="/waitlist/founding" element={<FoundingCheckout />} />
      <Route path="/waitlist/founding/success" element={<FoundingSuccess />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/meta" element={<MetaLanding />} />
      <Route path="/ads" element={<MetaLanding />} />
      {/* Spanish landing page */}
      <Route path="/es" element={<LandingES />} />
      <Route path="/media-kit" element={<ProtectedRoute><MediaKit /></ProtectedRoute>} />
      <Route path="/founder-kit" element={<FounderKit />} />
      {/* CPA Readiness Quiz Funnel — public, no auth required */}
      <Route path="/quiz" element={<QuizFunnel />} />
      <Route path="/quiz/success" element={<QuizFunnel />} />
      {/* Audit funnel pages */}
      <Route path="/audit" element={<AuditLanding />} />
      <Route path="/audit/checkout" element={<AuditCheckout />} />
      <Route path="/audit/founding" element={<AuditFounding />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  // Initialize mobile app features (push notifications, status bar, etc.)
  const { isInitialized, isNative } = useMobileAppInit();

  // Show loading screen while mobile features initialize
  if (isNative && !isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-white">Initializing app...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary message="The application encountered an unexpected error. Please refresh the page.">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
          {!isNative && <VersionChecker />} {/* Only check for updates on web */}
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// ── Version checker: polls /api/health for server restart, reloads on change ──
// Solves the "hard refresh required" issue:
//   - HMR WebSocket is blocked by the Kubernetes/Cloudflare proxy
//   - bundle.js has no content hash in dev mode
//   - React Router SPA navigation never re-downloads JS
//   Solution: detect new server deploy (started_at changes) → auto-reload
const POLL_INTERVAL_MS = 90_000; // check every 90 seconds

function VersionChecker() {
  const startedAtRef = useRef(null);
  const toastShownRef = useRef(false);

  useEffect(() => {
    let timer = null;

    const check = async () => {
      try {
        const res = await axios.get(`${API}/health`, { timeout: 8000 });
        const serverStartedAt = res.data?.started_at;
        if (!serverStartedAt) return;

        if (startedAtRef.current === null) {
          // First check — store the baseline
          startedAtRef.current = serverStartedAt;
          return;
        }

        if (serverStartedAt !== startedAtRef.current && !toastShownRef.current) {
          // Server restarted → new code deployed
          toastShownRef.current = true;
          toast.info("A new version of SolelyTax AI is available.", {
            description: "Click to reload and get the latest updates.",
            duration: 0,          // stay until dismissed or clicked
            action: {
              label: "Reload now",
              onClick: () => window.location.reload(),
            },
          });
          // Auto-reload after 10 seconds if user doesn't click
          setTimeout(() => window.location.reload(), 10_000);
        }
      } catch {
        // Silently ignore network errors — don't spam the user
      }
    };

    // Start polling
    check();
    timer = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  return null;
}

export default App;
